import { Grid, Typography } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { BasicButton } from '@tassat/button';
import { DeclineReasonCodeEnum, PledgeTransactionTypeEnum } from '@tassat/common-definitions';
import { SecurityCurrencyAssetPair } from '@tassat/fred-extended-definitions';
import { ButtonType, WalletAddress } from '@tassat/text';
import { convertToNumber, formatAsAsset, formatAsAssetNoSign } from '@tassat/utility';
import React from 'react';

import { ValidationData } from 'Reducers/validation';

import { ModalPledgeValues, PledgeModalAction } from './pledgeDetailsModal/pledgeContextWorkflow';
import type { PledgeResultModalProps } from './pledgeResultModal';
import { muiStyles } from './pledgeResultStyles';

interface PledgeResultSuccessProps extends WithStyles<typeof muiStyles> {
    actionType?: PledgeModalAction;
    assetPair: SecurityCurrencyAssetPair;
    onClose: () => void;
    modalPledgeValues: ModalPledgeValues | null;
    pledgeType: PledgeResultModalProps['pledgeType'];
    validation: ValidationData;
}

    const pledgeResultActionTitle = {
        [PledgeTransactionTypeEnum.new_pledge]: { submit: 'a post', action: 'a post request' },
        [PledgeTransactionTypeEnum.pledge_add_on]: { submit: 'an add on', action: 'an add on request' },
        [PledgeTransactionTypeEnum.reclaim]: { submit: 'a reclaim', action: 'a reclaim request' },
        [PledgeTransactionTypeEnum.partial_reclaim]: { submit: 'a reclaim', action: 'a partial reclaim request' },
        [PledgeTransactionTypeEnum.release]: { submit: 'a release', action: 'a release request' },
        [PledgeTransactionTypeEnum.partial_release]: { submit: 'a release', action: 'a partial release request' }
    };

export function PledgeResultSuccessComponent(props: PledgeResultSuccessProps) {
    const {
        classes,
        actionType,
        modalPledgeValues,
        pledgeType,
        validation,
        onClose,
        assetPair
    } = props;

    const fromApproval = React.useMemo(() => {
        return modalPledgeValues?.RequestId;
    }, [modalPledgeValues]);

    const isDeclined = React.useMemo(() => {
        return modalPledgeValues?.DeclineReason && Object.values(DeclineReasonCodeEnum)
            .includes(modalPledgeValues.DeclineReason) &&
            Number(modalPledgeValues.DeclineReason) !== DeclineReasonCodeEnum.SUCCESS &&
            Number(modalPledgeValues.DeclineReason) !== DeclineReasonCodeEnum.NONE;
    }, [modalPledgeValues]);

    const transactionType = React.useMemo(() => {
        return pledgeType;
    }, [ fromApproval, modalPledgeValues ]);

    const pledgeText = React.useMemo(() => {
        if (actionType === 'decline') {
            return <span style={{ color: '#CE4A4A' }}>declined</span>;
        } else if (actionType === 'cancel') {
            return <span style={{ color: '#757575' }}>canceled</span>;
        } else if (actionType === 'approve') {
            return 'approved';
        }

        return 'submitted';
    }, [actionType]);

    const pledgeRejectedText = React.useMemo(() => {
        return Number(modalPledgeValues?.DeclineReason) && !Object.values(DeclineReasonCodeEnum)
            .includes(Number(modalPledgeValues?.DeclineReason)) ? <span>but your post was <span style={{ color: '#CE4A4A' }}>REJECTED</span></span> : '';
    }, [modalPledgeValues]);

    if (!modalPledgeValues) return <div />;

    const renderSuccessText = () => {
        let pledgeAmountText: React.ReactNode;
        if (assetPair?.security) {
            pledgeAmountText = fromApproval ?
                modalPledgeValues.AssetQuantity &&
                `${formatAsAssetNoSign(convertToNumber(modalPledgeValues.AssetQuantity), assetPair.security, true)} ${assetPair.security?.Units} of ${assetPair.security?.Ticker}`
                : modalPledgeValues.AssetAmount &&
                    `${formatAsAssetNoSign(convertToNumber(modalPledgeValues.AssetAmount), assetPair.security, true)} ${assetPair.security?.Units} of ${assetPair.security?.Ticker}`;
        } else {
            pledgeAmountText = fromApproval ?
                formatAsAsset(modalPledgeValues.AssetQuantity, assetPair.currency, 'symbol')
                : formatAsAsset(modalPledgeValues.FiatAmount, assetPair.currency, 'symbol');
        }

        let recipientDisplay: React.ReactNode;
        if (modalPledgeValues.Recipient) {
            if (modalPledgeValues.Recipient.value.NickName) {
                recipientDisplay = modalPledgeValues.Recipient.value.NickName;
            } else {
                recipientDisplay = modalPledgeValues.Recipient.value.AccountName;
            }
        } else {
            recipientDisplay = (
                <WalletAddress
                    buttonType={ButtonType.SMALL}
                    isAdmin={false}
                    walletAddress={modalPledgeValues.RecipientAddress || ''}
                />
            );
        }

        switch (transactionType) {
            case PledgeTransactionTypeEnum.new_pledge:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='h5'
                        >
                            You have successfully {pledgeText} {pledgeResultActionTitle[transactionType][pledgeText === 'submitted' ? 'submit' : 'action']} of
                        </Typography>
                        <Typography
                            align='center'
                            className={classes.amountText}
                            variant='h4'
                        >
                            {pledgeAmountText}
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.largePaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            to
                        </Typography>
                        {!fromApproval &&
                            <Typography
                                align='center'
                                className={classes.paddedText}
                                variant='h5'
                            >
                                {recipientDisplay}
                            </Typography>}
                        {fromApproval &&
                            <Typography
                                align='center'
                                className={classes.paddedText}
                                variant='h5'
                            >
                                {modalPledgeValues.ReceivingAccountName}
                            </Typography>}
                        {pledgeRejectedText &&
                            <Typography
                                align='center'
                                className={`${classes.paddedText} ${classes.submitText}`}
                                variant='h5'
                            >
                                {pledgeRejectedText}
                            </Typography>}
                        {
                            !isDeclined &&
                            <Typography
                                align='center'
                                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                                variant='body1'
                            >
                                Requests are processed in real-time.
                            </Typography>
                        }
                    </>
                );
            case PledgeTransactionTypeEnum.pledge_add_on:
            case PledgeTransactionTypeEnum.reclaim:
            case PledgeTransactionTypeEnum.partial_reclaim:
            case PledgeTransactionTypeEnum.release:
            case PledgeTransactionTypeEnum.partial_release:
                return (
                    <>
                        <Typography
                            align='center'
                            className={`${classes.paddedText} ${classes.submitText}`}
                            variant='h5'
                        >
                            You have successfully {pledgeText} {pledgeResultActionTitle[transactionType][pledgeText === 'submitted' ? 'submit' : 'action']} of
                        </Typography>
                        <Typography
                            align='center'
                            className={classes.amountText}
                            variant='h4'
                        >
                            {
                                modalPledgeValues.AssetQuantity != null &&
                                (assetPair?.security ?
                                    `${formatAsAssetNoSign(convertToNumber(modalPledgeValues.AssetQuantity), assetPair.security, true)} ${assetPair.security?.Units} of ${assetPair.security?.Ticker}`
                                    : formatAsAsset(modalPledgeValues.AssetQuantity, assetPair.currency, 'symbol'))
                            }
                        </Typography>
                        <Typography
                            align='center'
                            className={`${classes.largePaddedText} ${classes.disclaimerText}`}
                            variant='body1'
                        >
                            to
                        </Typography>
                        {!fromApproval &&
                            <Typography
                                align='center'
                                className={classes.paddedText}
                                variant='h5'
                            >
                                {modalPledgeValues.PledgeParticipant?.[0].PledgeParticipantAccountName}
                            </Typography>}
                        {fromApproval &&
                            <Typography
                                align='center'
                                className={classes.paddedText}
                                variant='h5'
                            >
                                {modalPledgeValues.ReceivingAccountName}
                            </Typography>}
                        {pledgeRejectedText &&
                            <Typography
                                align='center'
                                className={`${classes.paddedText} ${classes.submitText}`}
                                variant='h5'
                            >
                                {pledgeRejectedText}
                            </Typography>}
                        {
                            !isDeclined &&
                            <Typography
                                align='center'
                                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                                variant='body1'
                            >
                                Requests are processed in real-time.
                            </Typography>
                        }
                    </>
                );
            default:
                return (
                    <Typography
                        align='center'
                        className={classes.paddedText}
                        variant='body1'
                    >
                        Your request was successful.
                    </Typography>
                );
        }
    };

    return (
        <Grid
            alignItems='center'
            className={classes.root}
            container
            direction='column'
            spacing={0}
        >
            {renderSuccessText()}
            <Typography
                align='center'
                className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                variant='body1'
            >
                View complete details in the {'post\'s'} activity section.
            </Typography>
            {modalPledgeValues.PledgeId != null &&
                <Typography
                    align='center'
                    className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                    variant='body1'
                >
                    Post ID: {modalPledgeValues.PledgeId}
                </Typography>}
            {validation.requestId &&
                <Typography
                    align='center'
                    className={`${classes.smallPaddedText} ${classes.disclaimerText}`}
                    variant='body1'
                >
                    Request ID: {validation.requestId}
                </Typography>}
            <Grid
                className={classes.buttonContainer}
                container
                direction='row'
                item
                justifyContent='center'
                spacing={0}
            >
                <BasicButton
                    onClick={onClose}
                    text='DONE'
                    type='main'
                />
            </Grid>
        </Grid>
    );
}

const PledgeResultSuccess = withStyles(muiStyles)(PledgeResultSuccessComponent);
export { PledgeResultSuccess };